import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Breadcrumbs,
  Divider,
  Grid,
  Link,
  Typography,
  AppBar,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
  LinearProgress,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { isEqual } from "lodash";
import { TabContext, TabPanel } from "@mui/lab";
import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import BubbleChartIcon from "@mui/icons-material/BubbleChart";
import { useLocation, useNavigate } from "react-router-dom";
import ContentWrapper from "../components/ContentWrapper";
import FilterNav from "../components/FilterNav";
import NoResults from "../components/NoResults";
import PageHeading from "../components/PageHeading";
import SaveSearch from "../components/SaveSearch";
import TableAdvanced from "../components/TableAdvanced";
import { spectrumPricingCells } from "../constants/tables";
import allRoutes from "../constants/routes";
import { useGetSpectrumPricingQuery, useLazyExportSpectrumPricingQuery } from "../store/apis/search";
import {
  selectSpectrumPricingSlice,
  setPage,
  setRowsPerPage,
  setSortBy,
  setSavedValues,
  setCurrentView,
} from "../store/slices/spectrumPricing";
import { selectUiState, setDense, setFontSize } from "../store/slices/ui";
import { selectExport } from "../store/slices/auth";
import { useSaveSearchMutation } from "../store/apis/save";
import { mapValuesToSaveData } from "../utils";
import BubbleChart from "../components/BubbleChart";
import Select from "../components/Select";

// Define an interface for the row data structure
interface SpectrumPricingRow {
  id: number;
  operator?: {
    id?: number;
    title?: string;
    country?: {
      title?: string;
    };
  };
  country?: {
    title?: string;
  };
  licence?: {
    title?: string;
  };
  frequency_bands?: {
    title?: string;
    range?: string;
    suffix?: string;
  }[];
  completed_year?: number;
  completed_month?: number;
  price_total?: number;
  price_population?: number;
  allocated_bandwidth?: number;
  children?: SpectrumPricingRow[];
  isExpanded?: boolean;
}

const SpectrumPricingSearch: React.FC = () => {
  const dispatch = useDispatch();
  const { dense, fontSize, exportFields } = useSelector(selectUiState);
  const { sortDesc, sortBy, rowsPerPage, page, values, lastSaved, currentView } = useSelector(selectSpectrumPricingSlice);
  const exportEnabled = useSelector(selectExport);
  const [saveSearch, { isLoading: isSaving }] = useSaveSearchMutation();
  const [trigger, { data: download, isLoading: isExporting }] = useLazyExportSpectrumPricingQuery();
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const {
    data = { results: [], total: 0 },
    isLoading,
    isFetching,
    isUninitialized,
  } = useGetSpectrumPricingQuery({
    rowsPerPage,
    page,
    values,
    sortBy,
    sortDirection: sortDesc ? "desc" : "asc",
  });

  const { results = [], total = 0 } = data;
  const pages = Math.ceil(total / rowsPerPage);
  const loading = isUninitialized || isLoading || isFetching;

  // IDs of rows that have children
  const rowIdsWithChildren = useMemo(
    () =>
      results
        .filter((row: SpectrumPricingRow) => row.children && row.children.length > 0)
        .map((row: SpectrumPricingRow) => row.id),
    [results],
  );

  // Auto-expand all rows with children when data is loaded
  useEffect(() => {
    if (!loading && rowIdsWithChildren.length > 0) {
      setExpandedRows(new Set(rowIdsWithChildren));
    }
  }, [rowIdsWithChildren, loading]);

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.up("sm"));
  const location = useLocation();
  const lastPart = location.pathname.split("/").pop() || "search";

  // Use the persisted view from Redux instead of local state
  const viewCurrent = currentView;

  const navigate = useNavigate();
  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    if (newValue === "bubble") {
      navigate(`${allRoutes.spectrum_pricing.path}/search/bubble`);
      dispatch(setCurrentView("bubble"));
    } else if (newValue === "search") {
      navigate(`${allRoutes.spectrum_pricing.path}/search`);
      dispatch(setCurrentView("search"));
    }
  };

  // Process the results to include expanded state
  const processedResults = useMemo(() => {
    const processRow = (row: SpectrumPricingRow): SpectrumPricingRow[] => {
      const hasChildren = row.children && row.children.length > 0;
      const isExpanded = hasChildren && expandedRows.has(row.id);

      const processedRow = {
        ...row,
        isExpanded,
      };

      if (hasChildren && isExpanded && row.children) {
        return [processedRow, ...row.children];
      }

      return [processedRow];
    };

    return results.flatMap(processRow);
  }, [results, expandedRows]);

  const handleRowClick = (isTotal: boolean, rowData: any) => {
    const row = rowData as SpectrumPricingRow;
    if (row.children && row.children.length > 0) {
      setExpandedRows(prev => {
        const newSet = new Set(prev);
        if (newSet.has(row.id)) {
          newSet.delete(row.id);
        } else {
          newSet.add(row.id);
        }
        return newSet;
      });
    }
  };

  const allExpanded =
    rowIdsWithChildren.length > 0 && rowIdsWithChildren.every((id: number) => expandedRows.has(id));

  const handleToggleExpandAll = (expanded: boolean) => {
    if (expanded) {
      setExpandedRows(new Set(rowIdsWithChildren));
    } else {
      setExpandedRows(new Set());
    }
  };

  const handleSave = (title: string) => {
    dispatch(setSavedValues(values));
    saveSearch({
      sectionId: 7,
      data: { title, ...mapValuesToSaveData(7, values) },
    });
  };

  const renderButtons = () => (
    <Box paddingY={4}>
      <Grid container spacing={4} alignItems="center">
        <Grid>
          <SaveSearch disabled={isEqual(values, lastSaved)} loading={isSaving} onClick={handleSave} />
        </Grid>
        {/*         <Grid>
          <ExportCsv
            disabled={exportEnabled ? false : total > 2000}
            source={download}
            loading={isExporting}
            onClick={format => trigger({ values: values, fields: exportFields, format })}
          />
        </Grid> */}
      </Grid>
    </Box>
  );

  useEffect(() => {
    // Update the persisted view when the URL changes (but only if it's different)
    if (lastPart !== currentView) {
      dispatch(setCurrentView(lastPart));
    }
  }, [lastPart, currentView, dispatch]);

  return (
    <>
      <Helmet title="Spectrum Pricing - GSA GAMBoD" />
      <FilterNav />
      <PageHeading
        title={allRoutes.spectrum_pricing.label}
        icon={allRoutes.spectrum_pricing.icon}
        color={allRoutes.spectrum_pricing.color}
        section="spectrum"
        inset
      >
        <Breadcrumbs aria-label="Breadcrumb">
          <Link href={allRoutes.dashboard.path}>Dashboard</Link>
          <Link href={allRoutes.spectrum_pricing.path}>Spectrum Pricing</Link>
          <Typography>Search</Typography>
        </Breadcrumbs>
      </PageHeading>
      <ContentWrapper inset>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid size="grow">
            <Box paddingY={10}>
              <Grid container spacing={8} alignItems="flex-end" wrap="nowrap">
                <Grid sx={{ display: { xs: "none", md: "block" } }}>
                  <Typography variant="h4">Your Results</Typography>
                </Grid>
                <Grid>
                  <Typography variant="subtitle2" gutterBottom>
                    Returned results
                  </Typography>
                  <Typography variant="h4">
                    <span data-testid="spectrumPricingTotal" data-testvalue={total}>
                      {total.toLocaleString()}
                    </span>{" "}
                    Spectrum Pricings
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid sx={{ display: { xs: "none", md: "block" } }}>{renderButtons()}</Grid>
        </Grid>
      </ContentWrapper>
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <ContentWrapper inset>
          <Divider />
          {renderButtons()}
        </ContentWrapper>
      </Box>
      <Divider />
      <TabContext value={lastPart}>
        <AppBar position="sticky" component="div" sx={{ backgroundColor: "white" }} elevation={0}>
          {loading ? (
            <Box width="100%" position="relative" height={4}>
              <LinearProgress />
            </Box>
          ) : (
            <Box width="100%" position="relative" style={{ background: "#fff" }} height={4}></Box>
          )}
          <ContentWrapper body inset>
            <Box paddingTop={3}>
              <Tabs value={lastPart} onChange={handleChange} indicatorColor="primary" textColor="primary">
                <Tab
                  label="Table"
                  value="search"
                  data-testid="table-tab"
                  icon={<CalendarViewMonthIcon />}
                  iconPosition="start"
                  style={{
                    paddingLeft: isTablet ? "40px" : "20px",
                    paddingRight: isTablet ? "40px" : "20px",
                  }}
                />
                <Tab
                  label="Bubble Chart"
                  value="bubble"
                  data-testid="bubble-chart-tab"
                  icon={<BubbleChartIcon />}
                  iconPosition="start"
                  style={{
                    minWidth: "180px",
                    paddingLeft: isTablet ? "40px" : "20px",
                    paddingRight: isTablet ? "40px" : "20px",
                  }}
                />
              </Tabs>
            </Box>
            <Divider />
          </ContentWrapper>
        </AppBar>
        <ContentWrapper body inset>
          <Box minHeight={500}>
            <TabPanel value="bubble" sx={{ p: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", p: 2, m: 10, mb: 0 }}>
                <Typography sx={{ mr: 5 }}>Show</Typography>
                <Select
                  name="rows-per-page-bubble"
                  value={rowsPerPage}
                  options={[
                    { label: "10", value: 10 },
                    { label: "20", value: 20 },
                    { label: "50", value: 50 },
                    { label: "100", value: 100 },
                    { label: "250", value: 250 },
                    { label: "500", value: 500 },
                  ]}
                  onChange={value => {
                    dispatch(setRowsPerPage(Number(value)));
                  }}
                />
                <Typography sx={{ mr: 2, ml: 3 }}>records</Typography>
              </Box>
              <Box sx={{ width: "100%", height: "100%", p: 2 }}>
                <BubbleChart loading={loading} data={results.slice((page - 1) * rowsPerPage, page * rowsPerPage)} />
              </Box>
            </TabPanel>

            <TabPanel value="search">
              {lastPart !== viewCurrent ? (
                <></>
              ) : !loading && !results.length ? (
                <NoResults />
              ) : (
                <TableAdvanced
                  title={`GAMBoD Spectrum Pricing Search - ${total.toLocaleString()} results, page ${page}`}
                  dense={dense}
                  rows={processedResults}
                  fontSize={fontSize}
                  cellsMap={spectrumPricingCells(sortBy, sortDesc)}
                  page={page}
                  pages={pages}
                  rowsPerPage={rowsPerPage}
                  loading={loading}
                  setRowsPerPage={value => {
                    dispatch(setRowsPerPage(value));
                  }}
                  setPage={value => {
                    dispatch(setPage(value));
                  }}
                  setFontSize={value => {
                    dispatch(setFontSize(value));
                  }}
                  setDense={value => {
                    dispatch(setDense(value));
                  }}
                  onOrder={value => {
                    dispatch(setSortBy(value));
                  }}
                  allExpanded={allExpanded}
                  onToggleAllExpand={handleToggleExpandAll}
                  onRowClick={handleRowClick}
                />
              )}
            </TabPanel>
          </Box>
        </ContentWrapper>
      </TabContext>
    </>
  );
};

export default SpectrumPricingSearch;
